import ReactDialKnob, { SkinWrap, useAngleUpdater } from "react-dial-knob";
import { useRef } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";
import { ArrowsRightLeftIcon } from "@heroicons/react/24/solid";
import { red } from "tailwindcss/colors";
import useSvgBlobAnimations from "./useSvgBlobAnimations";

export interface Props {
  width: number;
  step: number;
  pan: number;
  setPan: (pan: number) => void;
  failed?: boolean;
}

const min = 0;
const max = 360;
const grabberOffset = 55;

export default function PanKnob(props: Props) {
  const { width, step, pan, setPan, failed } = props;
  const [angle, setAngle] = useAngleUpdater(pan);
  const hasBeenInteractedWith = useRef(false);
  const { d, fill, stroke, translateX } = useSvgBlobAnimations(props);

  function valueToPan(value: number) {
    if (value <= 90 && value >= 0) {
      return value;
    }

    return max + value + step;
  }

  function panToValue(pan: number) {
    if (pan <= 90 && pan >= 0) {
      return pan;
    }

    if (pan < 180 && pan > 0) {
      return 90;
    }

    return (max - pan + step) * -1;
  }

  function limiter(value: number) {
    if (value > 90) {
      return 90;
    }

    if (value < -90) {
      return -90;
    }

    return value;
  }

  const circleDiameter = width * 0.666;

  return (
    <div
      className={classNames("overflow-hidden", {
        "pointer-events-none": failed,
      })}
      style={{
        // height: circleDiameter + grabberOffset,
        paddingLeft: grabberOffset,
        paddingRight: grabberOffset,
      }}
    >
      <div style={{ height: grabberOffset }} />

      <div
        className={"flex justify-center"}
        style={{
          height: width * 0.45,
        }}
      >
        <SkinWrap>
          <ReactDialKnob
            diameter={circleDiameter}
            min={min}
            max={max}
            step={step}
            onAngleChange={(x) => {
              // prevent it from going further then 90 degrees
              if (x < 270 && x > 90) {
                return 90;
              }

              setAngle(x);
            }}
            value={valueToPan(props.pan)}
            knobStyle={{ overflow: "visible" }}
            onInteractionChange={() => {
              hasBeenInteractedWith.current = true;
            }}
            onValueChange={(value) => {
              // prevent the component from calling onValueChange before the user touches it
              if (!hasBeenInteractedWith.current) {
                return;
              }

              setPan(limiter(panToValue(value)));
            }}
          >
            <div
              className={classNames(
                "group absolute left-1/2 -translate-x-1/2 cursor-pointer"
              )}
              style={{ width: circleDiameter, height: circleDiameter }}
            >
              <div
                className={classNames(
                  "group relative rounded-full border-4 border-white"
                )}
                style={{
                  width: circleDiameter - 2,
                  height: circleDiameter - 2,
                  left: 2,
                  transform: `rotate(${angle}deg)`,
                }}
              >
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div
                    className="absolute left-1/2 top-0 -translate-x-1/2 bg-white"
                    style={{ width: 4, height: circleDiameter / 2 }}
                  />

                  <div
                    className={
                      "absolute left-1/2 top-1/2 h-4  w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
                    }
                  />

                  <div
                    className={
                      "absolute -top-0 left-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50"
                    }
                  />

                  <div
                    className={
                      "absolute -top-0 left-1/2  flex h-12  w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black transition-transform group-hover:scale-105 group-active:scale-95"
                    }
                  >
                    <ArrowsRightLeftIcon className={"h-5 w-5"} />
                  </div>
                </motion.div>
              </div>

              <motion.svg
                viewBox="0 0 200 200"
                xmlns="http://www.w3.org/2000/svg"
                className={"absolute inset-0 -z-10"}
                style={{ transform: translateX }}
              >
                <motion.path
                  d={d}
                  transform="translate(100 100)"
                  style={{
                    fill: failed ? red[500] : fill,
                    stroke: failed ? red[500] : stroke,
                  }}
                />
              </motion.svg>

              <div
                className={
                  "absolute bottom-1/3 left-1/2 -translate-x-1/2 select-none p-1 text-center font-brik text-white"
                }
                style={{
                  fontSize: circleDiameter / 14,
                }}
              >
                {props.pan}
              </div>

              <div className={"absolute -inset-x-10 bottom-1/3 h-1 bg-white"} />
              <div
                className={
                  "absolute -inset-x-10 bottom-1/3 h-40 translate-y-full bg-black"
                }
              />
            </div>
          </ReactDialKnob>
        </SkinWrap>
      </div>
    </div>
  );
}
